import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RESCUE_STATUS_LABEL, type RescueRowDto, type RescueStatus } from '@coachrex/shared';
import { useRescue } from '../lib/api';
import { matchesRep, useRepFilter } from '../lib/repFilter';
import { money } from '../lib/format';
import { ErrorBox, PageHeader, Pills, RepFilter, Segmented, Skeleton } from '../components/ui';
import { RescueRow } from '../components/RescueRow';
import { StatusModal } from '../components/StatusModal';
import { useWatching } from '../lib/watching';
import { byBestChance } from '../lib/rescueSort';
import './rescue.css';

const TABS: RescueStatus[] = ['open', 'completed', 'recovered', 'lost', 'not_needed'];
type Sort = 'best' | 'value';

/** Handoff screen 04. */
export function Rescue() {
  const { data, error, isLoading } = useRescue();
  const rep = useRepFilter();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.includes(params.get('tab') as RescueStatus) ? params.get('tab') : 'open') as RescueStatus;
  const sort = (params.get('sort') === 'value' ? 'value' : 'best') as Sort;
  const setParam = (k: string, v: string, dflt: string) => {
    const next = new URLSearchParams(params);
    if (v === dflt) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };
  const [editing, setEditing] = useState<RescueRowDto | null>(null);
  const { watching, watch } = useWatching(data?.rows);

  const scoped = useMemo(() => (data?.rows ?? []).filter((r) => matchesRep(rep.value, r.rep?.id)), [data, rep.value]);
  const rows = useMemo(() => {
    const list = scoped.filter((r) => r.status === tab);
    return tab === 'open' || tab === 'completed' ? list.sort(sort === 'value' ? (a, b) => (b.value ?? 0) - (a.value ?? 0) : byBestChance) : list;
  }, [scoped, tab, sort]);

  const currency = data?.currency ?? 'USD';
  const weekAgo = Date.now() - 7 * 86_400_000;
  const atRisk = scoped.filter((r) => r.status === 'open').reduce((a, r) => a + (r.value ?? 0), 0);
  const recoveredWeek = scoped.filter((r) => r.status === 'recovered' && Date.parse(r.statusAt) >= weekAgo).reduce((a, r) => a + (r.value ?? 0), 0);

  return (
    <div className="page">
      <PageHeader
        title="Rescue"
        subtitle="Leads that showed intent on a call, text or email but didn’t book. Best chance first weighs each lead’s value by its grade. Coach Lex watches your CRM and moves leads along when a rep acts."
      />
      <div className="stat-cards">
        <Stat
          label="Open pipeline at risk"
          value={data ? money(atRisk, currency) : null}
          hint={data && !scoped.some((r) => r.status === 'open' && r.value) && scoped.some((r) => r.status === 'open') ? 'No opportunity values on these leads in your CRM yet.' : undefined}
        />
        <Stat label="Recovered this week" value={data ? money(recoveredWeek, currency) : null} />
        <Stat label="Avg time to first reply" value={data ? (data.avgFirstReplyMin == null ? '—' : fmtMin(data.avgFirstReplyMin)) : null} />
      </div>

      <div className="rescue-tools">
        <Pills options={TABS.map((t) => ({ value: t, label: RESCUE_STATUS_LABEL[t], count: scoped.filter((r) => r.status === t).length }))} value={tab} onChange={(t) => setParam('tab', t, 'open')} />
        <span className="rescue-tools__right">
          <RepFilter reps={data?.reps ?? []} />
          <Segmented
            label="Sort"
            options={[
              { value: 'best', label: 'Best chance first' },
              { value: 'value', label: 'Highest value' },
            ]}
            value={sort}
            onChange={(v) => setParam('sort', v, 'best')}
          />
        </span>
      </div>
      {RULE[tab] && <span className="rule-note">{RULE[tab]!(data?.backToOpenHours ?? 48)}</span>}

      <section className="card card--list">
        <div className="list-head rescue-grid">
          <span>Priority</span>
          <span>Lead</span>
          <span>Why it’s at risk</span>
          <span style={{ textAlign: 'right' }}>Value</span>
          <span style={{ textAlign: 'right' }}>Since</span>
          <span />
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={52} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="list-empty">{EMPTY[tab]}</div>
        ) : (
          rows.map((r) => <RescueRow key={r.id} r={r} currency={currency} watching={watching.has(r.id)} onOpenCrm={watch} onUpdate={setEditing} />)
        )}
      </section>
      {editing && <StatusModal item={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string | null; hint?: string }) {
  return (
    <section className="card stat-card">
      <span className="stat-card__label">{label}</span>
      {value == null ? <Skeleton height={36} width={140} /> : <span className="stat-card__value">{value}</span>}
      {hint && <span className="card-meta">{hint}</span>}
    </section>
  );
}

function fmtMin(m: number): string {
  if (m < 60) return `${m} min`;
  const h = m / 60;
  if (h < 24) return `${h < 10 ? h.toFixed(1) : Math.round(h)} hrs`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'}`;
}

const RULE: Partial<Record<RescueStatus, (backToOpenHours: number) => string>> = {
  completed: (h) => `A rep followed up in the CRM. If the lead doesn’t reply within ${h} hours it goes back to Open; after 2 returns with no reply it’s marked Lost.`,
  recovered: () => 'The lead reached your recovery milestone within 14 days of being flagged. Credit goes to the rep whose follow-up moved it out of Open.',
  lost: () => 'Marked lost after 2 returns to Open with no reply, or when a disqualifier was found. Managers can reopen with Update manually.',
};

const EMPTY: Record<RescueStatus, string> = {
  open: 'Nothing at risk right now. Coach Lex flags leads when a call is missed, a text or email waits past your reply target, or a call ends without a next step.',
  completed: 'No leads waiting on a reply after a follow-up.',
  recovered: 'No recoveries in the last 30 days yet.',
  lost: 'No lost leads in the last 30 days.',
  not_needed: 'Nothing marked as not needed in the last 30 days.',
};

